import React from 'react';

/**
 * THE supplier picker's dropdown list — one definition, two surfaces
 * (`OrderForm.js`'s Supplier field and `AdminSettings.js`'s "New Supplier" name
 * field). Presentational only: it owns no state, issues no request and decides
 * nothing about who may do what.
 *
 * ── THE MARKER, AND WHY IT IS ON ONE TIER ONLY ───────────────────────
 * The list merges two kinds of row (see `utils/supplierOptions.js`):
 *   a RECORD     — a real `suppliers` row, with an account number and a contact;
 *   a SUGGESTION — a name typed on a past order that reached approval, and
 *                  nothing else. It is NOT in the `suppliers` table.
 * Until you pick one they look identical, and the user cannot tell whether they
 * are getting a supplier we hold an account with or a string somebody typed.
 *
 * 🔑 **ONLY THE SUGGESTION TIER IS MARKED, and the choice is not arbitrary.**
 * Marking the records instead was considered and rejected on the measured shape
 * of the data: in the dev database, department 3 had 258 orders using 17
 * distinct typed supplier names and **ZERO rows in `suppliers`**, and only 2 of
 * 147 departments had any supplier records at all (measured 2026-08-08, recorded
 * in `server/helpers/supplierBackfill.js`). Marking records would therefore put
 * a badge on almost nothing while leaving every row in a typical department
 * unexplained; marking suggestions puts the caveat exactly where the missing
 * information is. It also means a RECORD renders byte-for-byte as it did before
 * this feature — nothing about the existing list changed appearance.
 *
 * 🎨 **It is TEXT, not a colour and not a coloured dot.** CLAUDE.md records the
 * failure both ways round: a colour that carries meaning needs its CASCADE
 * checked, not just its contrast (`index.css` carries global `!important`
 * overrides that have silently killed a semantic colour twice), and deleting one
 * of two redundant signals promotes the survivor to a standard it was never
 * measured against. Muted text has neither problem: it is the sole carrier, it
 * is legible to a screen reader as part of the option, and it needs no
 * WCAG 1.4.11 argument because it is not a non-text mark.
 *
 * The wording is `from past orders` — it says where the name came from, which
 * is also why it has no contact details, rather than labelling it with a piece
 * of internal vocabulary ("unsaved", "not a record") the user does not have.
 */

/**
 * The muted-hint colour pair — the house convention, and the same one
 * `OrderForm.js` uses for its "no suppliers match" hint two elements below this
 * list. The `dark:` variant is mandatory (CLAUDE.md, Colour Theme & Dark Mode).
 */
export const SUPPLIER_SUGGESTION_MARKER_CLASS =
  'ml-2 shrink-0 text-xs text-gray-500 dark:text-gray-400';

/** The marker's words, exported so tests and both surfaces share one string. */
export const SUPPLIER_SUGGESTION_MARKER = 'from past orders';

/**
 * @param {object}   props
 * @param {string}   props.id         the `<ul>` id an input's `aria-controls` points at
 * @param {object[]} props.options    rows from `buildSupplierOptions` (already filtered)
 * @param {Function} props.onSelect   called with the chosen NAME (a string, never a row)
 * @param {string}   [props.testId]   `data-testid` for the list itself
 */
function SupplierOptionList({ id, options = [], onSelect, testId = 'supplier-option-list' }) {
  return (
    <ul
      id={id}
      role="listbox"
      aria-label="Supplier suggestions"
      data-testid={testId}
      className="absolute z-10 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto"
    >
      {options.map((o) => (
        <li
          // The merged list is de-duplicated by `key` in buildSupplierOptions,
          // so this is unique BY CONSTRUCTION. A row `id` would not be — a
          // suggestion has none.
          key={o.key}
          className="flex items-center px-3 py-3 text-base text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 cursor-pointer min-h-[44px]"
          onClick={() => onSelect(o.name)}
          role="option"
          // `aria-selected` is required on every `role="option"`; nothing here
          // is ever pre-selected, so it is a constant false rather than absent.
          aria-selected={false}
          title={o.name}
          data-supplier-record={o.isRecord ? 'true' : undefined}
        >
          <span className="truncate">{o.name}</span>
          {!o.isRecord && (
            <span className={SUPPLIER_SUGGESTION_MARKER_CLASS}>
              {SUPPLIER_SUGGESTION_MARKER}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export default SupplierOptionList;
